import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import Dropdown from "@/components/ui/dropdown";
import DatePicker from "@/components/ui/datepicker";
import Table from "@/components/ui/table";
import Chip from "@/components/ui/chip";
import {
  Percentage,
  Tick,
  Document,
  User,
  Book,
  Target,
  ArrowRightLong,
  Calendar,
} from "@/components/icons";
import { fetchDepartments } from "@/features/announcements/services/departments.api";
import { educationManagementApi } from "@/features/education-management/services/education-management.api";
import { reportsApi } from "../services/reports.api";
import ReportKPICard from "../components/ReportKPICard";
import ReportKpiGrid from "../components/ReportKpiGrid";
import ReportSection from "../components/ReportSection";
import ReportChart from "../components/ReportChart";
import ReportCategoryComparison from "../components/ReportCategoryComparison";
import {
  REPORT_CATALOG,
  REPORT_CATEGORIES,
} from "../constants/report-catalog";
import type {
  ReportFilters,
  ReportsOverviewData,
  TrainingPerformanceItem,
  EducationPerformanceApiItem,
} from "../types/reports.types";
import type { TableColumn } from "@/types/component";
import styles from "./ReportsPage.module.scss";

const buildPersonnelTypeOptions = (t: (k: string) => string) => [
  { label: t("reports.all"), value: "all" },
  { label: t("reports.personnelTypes.personnel"), value: "personnel" },
  { label: t("reports.personnelTypes.intern"), value: "intern" },
];

function getCurrentMonthDateRange(): { dateFrom: string; dateTo: string } {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const pad = (n: number) => String(n).padStart(2, "0");
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return { dateFrom: fmt(firstDay), dateTo: fmt(lastDay) };
}

const defaultDateRange = getCurrentMonthDateRange();
const EDUCATION_PAGE_SIZE = 100;

/** Leaderboard satırı için eşlik renk. */
const rankAccent = (idx: number): "success" | "warning" | "danger" | "default" => {
  if (idx === 0) return "success";
  if (idx < 3) return "warning";
  if (idx > 6) return "danger";
  return "default";
};

const ReportsPage: React.FC = () => {
  const { t } = useTranslation();
  const PERSONNEL_TYPE_OPTIONS = useMemo(() => buildPersonnelTypeOptions(t), [t]);
  const navigate = useNavigate();
  const location = useLocation();

  const initialTab: "overview" | "education" = location.pathname.endsWith(
    "/education-performance",
  )
    ? "education"
    : "overview";
  const [activeTab, setActiveTab] = useState<"overview" | "education">(
    initialTab,
  );
  const [filters, setFilters] = useState<ReportFilters>({
    dateFrom: defaultDateRange.dateFrom,
    dateTo: defaultDateRange.dateTo,
    personnelType: "all",
  });

  const [overviewData, setOverviewData] =
    useState<ReportsOverviewData | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);

  const [educationPerformanceList, setEducationPerformanceList] = useState<
    TrainingPerformanceItem[]
  >([]);
  const [educationPerformanceLoading, setEducationPerformanceLoading] =
    useState(false);

  const [departmentOptions, setDepartmentOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [educationOptions, setEducationOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    fetchDepartments({ limit: 100 })
      .then((list) =>
        setDepartmentOptions(list.map((d) => ({ label: d.name, value: d.id }))),
      )
      .catch(() => setDepartmentOptions([]));

    educationManagementApi
      .getAll({ pageSize: EDUCATION_PAGE_SIZE })
      .then((res) => {
        setEducationOptions(
          res.data.map((e) => ({
            label: e.educationName ?? e.id,
            value: e.id,
          })),
        );
      })
      .catch(() => setEducationOptions([]));
  }, []);

  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const res = await reportsApi.getOverview(filters);
      setOverviewData(res?.data ?? null);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t("reports.errors.summaryLoad"),
      );
      setOverviewData(null);
    } finally {
      setOverviewLoading(false);
    }
  }, [filters]);

  const fetchEducationPerformance = useCallback(async () => {
    setEducationPerformanceLoading(true);
    try {
      const res = await reportsApi.getEducationPerformance(filters);
      const raw = res?.data ?? [];
      const items: TrainingPerformanceItem[] = raw.map(
        (r: EducationPerformanceApiItem) => ({
          educationId: r.education?.id ?? "",
          educationName: r.education?.name ?? "",
          enrolledCount: r.enrolledCount ?? 0,
          completedCount: r.completedCount ?? 0,
          completionPercent: r.completionPercent ?? 0,
          averageScore: r.averageScore ?? undefined,
          categoryNames:
            r.category?.map((c) => c.name).filter(Boolean).join(", ") ||
            undefined,
        }),
      );
      setEducationPerformanceList(items);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t("reports.errors.performanceLoad"),
      );
      setEducationPerformanceList([]);
    } finally {
      setEducationPerformanceLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);
  useEffect(() => {
    fetchEducationPerformance();
  }, [fetchEducationPerformance]);

  const handleExport = async (format: "excel" | "pdf") => {
    setIsExporting(true);
    try {
      const blob = await reportsApi.exportReport({ format, ...filters });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rapor-${new Date().toISOString().slice(0, 10)}.${
        format === "excel" ? "xlsx" : "pdf"
      }`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("reports.downloadSuccess"));
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t("reports.errors.downloadFailed"),
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handleResetFilters = () => {
    const r = getCurrentMonthDateRange();
    setFilters({
      dateFrom: r.dateFrom,
      dateTo: r.dateTo,
      personnelType: "all",
    });
  };

  const summary = overviewData?.summary;
  const departmentItems =
    overviewData?.departmentSuccessFailure?.items ?? [];
  const certificateItems = (
    overviewData?.certificateStatus?.items ?? []
  ).filter(
    (it) => String(it.label ?? "").trim().toLowerCase() !== "beklemede",
  );
  const participationItems =
    overviewData?.participationCompletionByMonth?.items ?? [];
  const trainingList =
    educationPerformanceList.length > 0
      ? educationPerformanceList
      : overviewData?.trainingPerformanceTable?.items ?? [];

  // Aggregate KPIs derived from data
  const totalEnrollments = useMemo(
    () =>
      trainingList.reduce((acc, it) => acc + (it.enrolledCount ?? 0), 0),
    [trainingList],
  );
  const totalCompleted = useMemo(
    () =>
      trainingList.reduce((acc, it) => acc + (it.completedCount ?? 0), 0),
    [trainingList],
  );
  const educationCount = trainingList.length;
  const avgCompletionPercent =
    summary?.generalCompletionRatePercent ??
    (totalEnrollments > 0
      ? Math.round((totalCompleted / totalEnrollments) * 100)
      : 0);

  // Top 10 leaderboard
  const leaderboard = useMemo(() => {
    const filtered = trainingList.filter((x) => x.enrolledCount > 0);
    return [...filtered]
      .sort(
        (a, b) =>
          (b.completionPercent ?? 0) - (a.completionPercent ?? 0) ||
          (b.completedCount ?? 0) - (a.completedCount ?? 0),
      )
      .slice(0, 10);
  }, [trainingList]);

  // Chart: Monthly participation & completion
  const monthlyChartSeries = useMemo(() => {
    const months = participationItems.map((m) => m.month ?? "—");
    const enrolled = participationItems.map((m) => m.enrolled ?? 0);
    const completed = participationItems.map((m) => m.completed ?? 0);
    return {
      categories: months,
      series: [
        { name: t("reports.chartLabels.enrolled"), data: enrolled },
        { name: "Tamamlayan", data: completed },
      ],
    };
  }, [participationItems]);

  // Chart: Department success/failure
  const departmentChartSeries = useMemo(() => {
    const topDepartments = [...departmentItems]
      .sort((a, b) => b.successPercent - a.successPercent)
      .slice(0, 10);
    return {
      categories: topDepartments.map((d) => d.departmentName),
      series: [
        {
          name: t("reports.chartLabels.successPct"),
          data: topDepartments.map((d) => d.successPercent),
        },
        {
          name: t("reports.chartLabels.failurePct"),
          data: topDepartments.map((d) => d.failurePercent),
        },
      ],
    };
  }, [departmentItems]);

  // Chart: Certificate distribution donut
  const certificateChartSeries = useMemo(
    () => certificateItems.map((i) => i.count),
    [certificateItems],
  );
  const certificateChartLabels = useMemo(
    () => certificateItems.map((i) => i.label ?? "—"),
    [certificateItems],
  );

  // Related reports (excluding overview itself)
  const relatedReports = REPORT_CATALOG.filter(
    (r) => r.key !== "overview" && r.isImplemented,
  );

  const educationColumns: TableColumn<TrainingPerformanceItem>[] = [
    {
      header: t("reports.tableHeaders.educationName"),
      accessor: (r) => (
        <div className={styles.eduNameCell}>
          <span className={styles.eduTitle}>
            {r.educationName || "—"}
          </span>
          {r.categoryNames && (
            <span className={styles.eduCat}>{r.categoryNames}</span>
          )}
        </div>
      ),
    },
    {
      header: "KAYIT",
      accessor: (r) => r.enrolledCount ?? 0,
    },
    {
      header: "TAMAMLAYAN",
      accessor: (r) => r.completedCount ?? 0,
    },
    {
      header: "TAMAMLAMA",
      accessor: (r) => (
        <div className={styles.barCell}>
          <div className={styles.barTrack}>
            <div
              className={styles.barFill}
              style={{
                width: `${Math.min(Math.max(r.completionPercent ?? 0, 0), 100)}%`,
              }}
            />
          </div>
          <span className={styles.barValue}>
            %{r.completionPercent ?? 0}
          </span>
        </div>
      ),
    },
    {
      header: "ORT. PUAN",
      accessor: (r) =>
        r.averageScore != null ? (
          <Chip
            size="small"
            variant="soft"
            type={
              (r.averageScore ?? 0) >= 80
                ? "success"
                : (r.averageScore ?? 0) >= 60
                  ? "warning"
                  : "error"
            }
          >
            {Math.round(Number(r.averageScore))}
          </Chip>
        ) : (
          "—"
        ),
    },
    {
      header: t("reports.tableHeaders.certificate"),
      accessor: (r) => r.certificateCount ?? "—",
    },
  ];

  const handleRowClick = (row: TrainingPerformanceItem) => {
    if (row.educationId)
      navigate(`/dashboard/reports/education/${row.educationId}`);
  };

  return (
    <DashboardLayout>
      <Header title={t("reports.title")} />

      <div className={styles.page}>
        {/* Hero */}
        <div className={styles.hero}>
          <div className={styles.heroLeft}>
            <h1 className={styles.heroTitle}>{t("reports.dashboardTitle")}</h1>
            <p className={styles.heroSubtitle}>
              Eğitim katılım, tamamlanma, sertifika ve birim başarısının
              birleşik görünümü. Alt raporlar için kartlardan drilldown yapın.
            </p>
          </div>
          <div className={styles.heroStats}>
            <div className={styles.heroStat}>
              <span className={styles.heroStatLabel}>{t("reports.stats.totalEducation")}</span>
              <span className={styles.heroStatValue}>
                {educationCount || "—"}
              </span>
            </div>
            <div className={styles.heroStat}>
              <span className={styles.heroStatLabel}>{t("reports.stats.totalEnrollment")}</span>
              <span className={styles.heroStatValue}>
                {totalEnrollments.toLocaleString("tr-TR")}
              </span>
            </div>
            <div className={styles.heroStat}>
              <span className={styles.heroStatLabel}>Tamamlayan</span>
              <span className={styles.heroStatValue}>
                {totalCompleted.toLocaleString("tr-TR")}
              </span>
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className={styles.filterBar}>
          <div className={styles.filterFields}>
            <div className={styles.filterField}>
              <label>{t("pageHeader.startDate")}</label>
              <DatePicker
                value={filters.dateFrom ? new Date(filters.dateFrom) : null}
                onChange={(d) =>
                  setFilters((p) => ({
                    ...p,
                    dateFrom:
                      d instanceof Date
                        ? d.toISOString().slice(0, 10)
                        : undefined,
                  }))
                }
                format="yyyy-MM-dd"
              />
            </div>
            <div className={styles.filterField}>
              <label>{t("pageHeader.endDate")}</label>
              <DatePicker
                value={filters.dateTo ? new Date(filters.dateTo) : null}
                onChange={(d) =>
                  setFilters((p) => ({
                    ...p,
                    dateTo:
                      d instanceof Date
                        ? d.toISOString().slice(0, 10)
                        : undefined,
                  }))
                }
                format="yyyy-MM-dd"
              />
            </div>
            <div className={styles.filterField}>
              <label>Personel Tipi</label>
              <Dropdown
                options={PERSONNEL_TYPE_OPTIONS}
                value={filters.personnelType ?? "all"}
                onSelect={(v) =>
                  setFilters((p) => ({
                    ...p,
                    personnelType:
                      (v as "all" | "personnel" | "intern") ?? "all",
                  }))
                }
                placeholder={t("reports.select")}
                selectedLabel={
                  PERSONNEL_TYPE_OPTIONS.find(
                    (o) => o.value === (filters.personnelType ?? "all"),
                  )?.label ?? ""
                }
              />
            </div>
            <div className={styles.filterField}>
              <label>Birim</label>
              <Dropdown
                options={[
                  { label: t("reports.all"), value: "" },
                  ...departmentOptions,
                ]}
                value={filters.departmentIds?.[0] ?? ""}
                onSelect={(v) =>
                  setFilters((p) => ({
                    ...p,
                    departmentIds:
                      v && typeof v === "string" ? [v] : undefined,
                  }))
                }
                placeholder={t("reports.all")}
                selectedLabel={
                  departmentOptions.find(
                    (o) => o.value === filters.departmentIds?.[0],
                  )?.label ?? t("reports.all")
                }
                searchable
              />
            </div>
            <div className={styles.filterField}>
              <label>{t("reportsEducation.label")}</label>
              <Dropdown
                options={[{ label: t("reports.all"), value: "" }, ...educationOptions]}
                value={filters.educationIds?.[0] ?? ""}
                onSelect={(v) =>
                  setFilters((p) => ({
                    ...p,
                    educationIds:
                      v && typeof v === "string" ? [v] : undefined,
                  }))
                }
                placeholder={t("reports.all")}
                selectedLabel={
                  educationOptions.find(
                    (o) => o.value === filters.educationIds?.[0],
                  )?.label ?? t("reports.all")
                }
                searchable
              />
            </div>
            <div className={`${styles.filterField} ${styles.filterFieldWide}`}>
              <label>Arama</label>
              <Input
                value={filters.search ?? ""}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, search: e.target.value }))
                }
                placeholder={t("reports.placeholders.educationName")}
                variant="outlined"
              />
            </div>
          </div>
          <div className={styles.filterActions}>
            <Button variant="outlined" onClick={handleResetFilters}>
              Sıfırla
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => handleExport("excel")}
              disabled={isExporting}
            >
              Excel İndir
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleExport("pdf")}
              disabled={isExporting}
            >
              PDF İndir
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            type="button"
            className={`${styles.tab} ${
              activeTab === "overview" ? styles.tabActive : ""
            }`}
            onClick={() => setActiveTab("overview")}
          >
            Genel Özet
          </button>
          <button
            type="button"
            className={`${styles.tab} ${
              activeTab === "education" ? styles.tabActive : ""
            }`}
            onClick={() => setActiveTab("education")}
          >
            Eğitim Performansı
          </button>
        </div>

        {activeTab === "overview" && (
          <>
            {/* KPI Strip */}
            <ReportKpiGrid columns={4}>
              <ReportKPICard
                title="Genel Tamamlama"
                value={
                  summary?.generalCompletionRatePercent != null
                    ? `%${summary.generalCompletionRatePercent}`
                    : "—"
                }
                icon={<Percentage width={16} height={16} />}
                accent="success"
                footnote={t("reports.footnotes.selectedRange")}
              />
              <ReportKPICard
                title={t("reports.stats.averageSuccessScore")}
                value={
                  summary?.averageSuccessScore != null
                    ? String(summary.averageSuccessScore)
                    : "—"
                }
                icon={<Tick width={16} height={16} />}
                accent="default"
                footnote={t("reports.footnotes.examAverage")}
              />
              <ReportKPICard
                title="Sertifika"
                value={
                  summary?.certificateDistribution
                    ? `${summary.certificateDistribution.issued} / ${summary.certificateDistribution.total}`
                    : "—"
                }
                icon={<Document width={16} height={16} />}
                accent="warning"
                footnote="Verilen / toplam"
              />
              <ReportKPICard
                title={t("reports.stats.activeUser")}
                value={
                  summary?.activeUserCount
                    ? `${summary.activeUserCount.active} / ${summary.activeUserCount.total}`
                    : "—"
                }
                icon={<User width={16} height={16} />}
                accent="default"
                footnote={t("reports.footnotes.recentActives")}
              />
            </ReportKpiGrid>

            <ReportKpiGrid columns={4}>
              <ReportKPICard
                title={t("reports.stats.totalEducation")}
                value={educationCount.toLocaleString("tr-TR")}
                icon={<Book width={16} height={16} />}
              />
              <ReportKPICard
                title={t("reports.stats.totalEnrollment")}
                value={totalEnrollments.toLocaleString("tr-TR")}
                icon={<User width={16} height={16} />}
              />
              <ReportKPICard
                title="Tamamlayan"
                value={totalCompleted.toLocaleString("tr-TR")}
                icon={<Tick width={16} height={16} />}
                accent="success"
              />
              <ReportKPICard
                title="Hesaplanan Tamamlama %"
                value={`%${avgCompletionPercent}`}
                icon={<Target width={16} height={16} />}
                accent={
                  avgCompletionPercent >= 75
                    ? "success"
                    : avgCompletionPercent >= 50
                      ? "warning"
                      : "danger"
                }
                footnote={t("reports.footnotes.completedPerEnrolled")}
              />
            </ReportKpiGrid>

            {/* Kategori karşılaştırma (hiyerarşik) */}
            <ReportCategoryComparison filters={filters} />

            {/* Charts row */}
            <div className={styles.chartGrid}>
              <div className={styles.chartGridWide}>
                <ReportChart
                  title={t("reports.charts.monthlyAttendance")}
                  subtitle={t("reports.charts.monthlyAttendanceSubtitle")}
                  type="area"
                  height={300}
                  isLoading={overviewLoading}
                  isEmpty={participationItems.length === 0}
                  emptyMessage={t("reports.charts.monthlyAttendanceEmpty")}
                  series={monthlyChartSeries.series}
                  options={{
                    xaxis: { categories: monthlyChartSeries.categories },
                    stroke: { curve: "smooth", width: 2 },
                    fill: {
                      type: "gradient",
                      gradient: { opacityFrom: 0.45, opacityTo: 0 },
                    },
                    colors: ["#4f46e5", "#10b981"],
                    yaxis: { labels: { formatter: (v) => String(v) } },
                  }}
                />
              </div>
              <div>
                <ReportChart
                  title={t("reports.charts.certificateDistribution")}
                  subtitle={t("reports.charts.certificateDistributionSubtitle")}
                  type="donut"
                  height={300}
                  isLoading={overviewLoading}
                  isEmpty={certificateItems.length === 0}
                  emptyMessage="Sertifika verisi yok."
                  series={certificateChartSeries}
                  options={{
                    labels: certificateChartLabels,
                    colors: ["#10b981", "#4f46e5", "#f59e0b", "#64748b"],
                    legend: { position: "bottom" },
                    plotOptions: {
                      pie: { donut: { size: "62%" } },
                    },
                  }}
                />
              </div>
            </div>

            <ReportChart
              title={t("reports.charts.departmentSuccess")}
              subtitle={t("reports.charts.departmentSuccessSubtitle")}
              type="bar"
              height={380}
              isLoading={overviewLoading}
              isEmpty={departmentItems.length === 0}
              emptyMessage="Birim verisi yok."
              series={departmentChartSeries.series}
              options={{
                plotOptions: {
                  bar: {
                    horizontal: true,
                    barHeight: "65%",
                    borderRadius: 4,
                  },
                },
                xaxis: {
                  categories: departmentChartSeries.categories,
                  labels: { formatter: (v) => `${v}%` },
                  max: 100,
                },
                colors: ["#10b981", "#ef4444"],
                legend: { position: "top", horizontalAlign: "right" },
                stroke: { show: false },
                dataLabels: {
                  enabled: true,
                  formatter: (v) => `${v}%`,
                  style: { fontSize: "11px", fontWeight: 600 },
                },
              }}
            />

            {/* Leaderboard */}
            <ReportSection
              title={t("reports.topEducations.title")}
              description={t("reports.topEducations.description")}
              icon={<Target width={18} height={18} />}
              padded={false}
              actions={
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setActiveTab("education")}
                >
                  Tüm Eğitimleri Gör
                </Button>
              }
            >
              {leaderboard.length === 0 ? (
                <div className={styles.emptyInset}>
                  Seçilen aralıkta eğitim verisi bulunamadı.
                </div>
              ) : (
                <ol className={styles.leaderboard}>
                  {leaderboard.map((item, idx) => {
                    const accent = rankAccent(idx);
                    return (
                      <li
                        key={item.educationId || idx}
                        className={`${styles.leaderItem} ${styles[`rank_${accent}`]}`}
                        onClick={() =>
                          item.educationId &&
                          navigate(
                            `/dashboard/reports/education/${item.educationId}`,
                          )
                        }
                      >
                        <span className={styles.leaderRank}>{idx + 1}</span>
                        <div className={styles.leaderBody}>
                          <span className={styles.leaderName}>
                            {item.educationName || "—"}
                          </span>
                          <span className={styles.leaderMeta}>
                            {item.categoryNames ?? t("reports.placeholders.categoryNotSpecified")}
                            {" · "}
                            {item.enrolledCount} kayıt
                            {" · "}
                            {item.completedCount ?? 0} tamamlayan
                          </span>
                        </div>
                        <div className={styles.leaderRight}>
                          <span className={styles.leaderValue}>
                            %{item.completionPercent ?? 0}
                          </span>
                          <ArrowRightLong
                            width={18}
                            height={18}
                            color="#94a3b8"
                          />
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </ReportSection>

            {/* Related reports drilldown */}
            <ReportSection
              title={t("reports.detailReports.title")}
              description={t("reports.detailReports.description")}
              icon={<Calendar width={18} height={18} />}
            >
              <div className={styles.categoryGrid}>
                {REPORT_CATEGORIES.map((cat) => {
                  const items = relatedReports.filter(
                    (r) => r.category === cat.key,
                  );
                  if (items.length === 0) return null;
                  return (
                    <div key={cat.key} className={styles.categoryBlock}>
                      <div className={styles.categoryHead}>
                        <span className={styles.categoryTitle}>
                          {cat.title}
                        </span>
                        <span className={styles.categoryDesc}>
                          {cat.description}
                        </span>
                      </div>
                      <div className={styles.reportLinks}>
                        {items.map((r) => (
                          <button
                            key={r.key}
                            type="button"
                            className={styles.reportLink}
                            onClick={() => navigate(r.route)}
                          >
                            <span className={styles.reportLinkTitle}>
                              {r.title}
                            </span>
                            <span className={styles.reportLinkDesc}>
                              {r.description}
                            </span>
                            <ArrowRightLong
                              width={16}
                              height={16}
                              color="#e7212b"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ReportSection>
          </>
        )}

        {activeTab === "education" && (
          <ReportSection
            title={t("reports.performanceList.title")}
            description={t("reports.performanceList.description")}
            icon={<Book width={18} height={18} />}
            padded={false}
          >
            <div className={styles.tableWrap}>
              <Table
                columns={educationColumns}
                data={trainingList}
                isLoading={educationPerformanceLoading}
                getRowId={(r) => r.educationId}
                onRowClick={handleRowClick}
                emptyMessage={t("reports.performanceList.empty")}
              />
            </div>
          </ReportSection>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ReportsPage;
